import { DateTime } from "luxon";
import { formatDate, formatTime } from "../../../shared/dateTimeUtils";
import { Status } from "../SnippetReport";
import React from "react";

/**
 * @description If the snippet report is scoped to a facility, add "For" as a prefix
 * @param {*} facility
 */
const withFacility = (facility, isMulti, isLast) => {
  if (facility) {
    if (!isMulti) {
      return `For ${facility}`;
    } else {
      return !isLast ? ` ${facility}` : ` & ${facility}`;
    }
  }
  return "";
};

/**
 * @description If the snippet report is scoped to a unit within a facility, add "in" as a prefix
 * or "In" if facility is undefined. Otherwise, display "in all units" if units is undefined.
 * @param {*} facility
 * @param {*} unit
 */
const withUnit = (facility, unit, isMulti, isLast) => {
  if (unit) {
    if (facility) {
      return ` - ${unit}`;
    } else {
      let unitStatement;
      if (!isMulti) {
        unitStatement = `In ${unit}`;
      } else {
        unitStatement = !isLast ? `${unit}` : ` & ${unit}`;
      }
      return unitStatement;
    }
  } else {
    let allUnits = "in all units";
    if (!facility) {
      allUnits = isMulti ? "all units" : "In all units";
    }
    return allUnits;
  }
};

/**
 * @description At the end of the statement, display "where" if there are filters applied
 * @param {*} columns
 */
const withFilters = ({ columns }) => {
  return columns?.filter((column) => column.filterValues.length > 0).length
    ? " where:"
    : ":";
};

/**
 * @description Displays the date range of the report in the format "from dd/mm/yyyy to dd/mm/yyyy"
 * If isCSV is set to true,
 * displays the date range in the format "from dd/mm/yyyy - hh:mm:ss to dd/mm/yyyy - hh:mm:ss"
 * @param {Date} startDate start date
 * @param {Date} endDate end date
 */
const withDuration = ({ startDate, endDate }) => {
  const start = DateTime.fromJSDate(startDate).startOf("day");
  const end =
    DateTime.fromJSDate(endDate) < DateTime.local()
      ? DateTime.fromJSDate(endDate).endOf("day")
      : DateTime.local();
  return (
    <>
      from {formatDate(start.toISO())}
      <span className="csv-only"> - {formatTime(start.toISO())}</span> to{" "}
      {formatDate(end.toISO())}
      <span className="csv-only"> - {formatTime(end.toISO())}</span>
    </>
  );
};

/**
 * @description Concatenates withFacility, withUnit, and withDuration
 * @param {*} selectedLocations
 * @param {*} startDate
 * @param {*} endDate
 */
const snippetMeta = ({
  selectedLocations,
  startDate,
  endDate,
  selectedBedList,
}) => {
  let locationTxt = "all units";
  if (!selectedLocations.length) {
    locationTxt = "In all units";
  }
  const isMulti = selectedLocations.length > 1;
  if (selectedBedList) {
    locationTxt = selectedBedList.listName;
  } else if (selectedLocations.length) {
    locationTxt = selectedLocations
      .map((s) => s.value || s)
      .map((selectedLocation, i) => {
        const { facility, unit } = selectedLocation;
        const isLast = i === selectedLocations.length - 1;
        return `${withFacility(facility, isMulti, isLast)} ${withUnit(
          facility,
          unit,
          isMulti,
          isLast,
        )}`;
      })
      .join(", ");
  }
  return (
    <span data-testid="summary">
      {isMulti ? "In " : ""}
      {locationTxt},{" "}
      {withDuration({
        startDate,
        endDate,
      })}
    </span>
  );
};

/**
 * @description Loops through all of the column data and builds a structure to display filters applied.
 * @param {*} columns
 * @param {*} startBed
 * @param {*} endBed
 * @returns {Array} array of filters applied keyed by column header
 */
const getFilterMap = ({ columns, startBed, endBed }) => {
  const filterMap = [];
  if (!columns) return [];
  columns?.forEach((column, i) => {
    if (column.filterValues?.length) {
      let filterName = column.Header;
      const filterValues = column.filterValues
        .filter(Boolean)
        .map((filterValue) => {
          if (filterValue && filterValue.label) {
            if (
              typeof filterValue.label === "string" &&
              filterName !== "Status"
            ) {
              return filterValue.label;
            } else {
              return Status({ status: filterValue.value || "" });
            }
          }
        });

      filterMap[i] = {
        filterName,
        filterValues: filterValues.map((filterValue) => filterValue),
      };
    }
  });
  if (startBed) {
    const bedFilterValues = [];
    if (startBed) {
      bedFilterValues.push(`Start Bed: ${startBed}`);
      if (endBed) {
        bedFilterValues.push(`End Bed: ${endBed}`);
      }
    }
    filterMap.push({
      filterName: "Bed",
      filterValues: bedFilterValues,
    });
  }
  return filterMap;
};

export { snippetMeta, withFilters, getFilterMap };
